import { DecimalPipe } from '@angular/common';
import { Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Message } from 'primeng/message';
import { RatingCounts } from '@/lib-backend';
import { averageRating, buildBellDistribution } from './review-distribution';

/**
 * Presentational form for the fake-review rating distribution: target average,
 * count, concentration and the created-at range, with a live preview of the
 * resulting per-rating bucket counts. It holds no business logic and performs
 * no API calls — the host owns the action buttons and reads the bound values.
 *
 * The five inputs are two-way model signals; {@link tags} and {@link validation}
 * are read-only display hints. When `tags` is null the tag pool box is hidden
 * (used by bulk generation, where each game resolves its own tags).
 */
@Component({
    selector: 'app-review-distribution-form',
    standalone: true,
    imports: [DecimalPipe, FormsModule, DatePicker, InputNumber, Message],
    templateUrl: './review-distribution-form.html',
})
export class ReviewDistributionForm {
    readonly targetAverage = model(4.5);
    readonly count = model(10);
    /** 1 = minimum spread (only floor/ceil of target), 0 = full diffusion. */
    readonly concentration = model(1);
    readonly createdFrom = model<Date | null>(null);
    readonly createdTo = model<Date | null>(null);

    /** When non-null, render the read-only tag pool box. */
    readonly tags = input<string[] | null>(null);
    /** Optional warning shown under the preview (e.g. pool validation). */
    readonly validation = input<string | null>(null);

    /** Unique input-id prefix so multiple instances don't collide on labels. */
    readonly idPrefix = input('rdf');

    protected readonly distribution = computed<RatingCounts>(() =>
        buildBellDistribution(
            this.targetAverage(),
            this.count(),
            this.concentration(),
        ),
    );

    protected readonly achievedAverage = computed(() =>
        averageRating(this.distribution()),
    );

    protected readonly maxBucket = computed(() => {
        const d = this.distribution();
        return Math.max(
            d.Rating1,
            d.Rating2,
            d.Rating3,
            d.Rating4,
            d.Rating5,
            1,
        );
    });

    protected onConcentrationInput(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        if (Number.isFinite(value)) {
            this.concentration.set(value);
        }
    }
}
